import { DocumentNode, gql } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import Attachment from '../../../../Attachment';
import {
	getFilename,
	getIconTypeForDocument,
} from '../../../../../lib/helpers';
import styles from './InvoiceAttachment.module.scss';
import {
	InvoiceAttachmentInvoiceFragment,
	useInvoiceAttachment_GetInvoiceWhereLazyQuery,
} from '#types';
import { Icon } from '../../../../../blocks/icons';
import classNames from 'classnames';

const invoiceAttachmentInvoiceFragment = gql`
	fragment invoiceAttachmentInvoice on Invoice {
		id
		uploadAttachment
		attachmentUrl
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InvoiceAttachment_getInvoiceWhereQuery = gql`
	query invoiceAttachment_getInvoiceWhere(
		$where: InvoiceWhereUniqueInput!
		$data: InvoiceTokenInput
	) {
		getInvoiceWhere(where: $where, data: $data) {
			invoice {
				...invoiceAttachmentInvoice
			}
		}
	}
	${ invoiceAttachmentInvoiceFragment }
`;

export type InvoiceAttachmentProps = {
	invoice?: InvoiceAttachmentInvoiceFragment;
	guestToken?: string;
	fileOrFilename?: File | string | undefined;
	className?: string;
};

type InvoiceAttachmentPropsWithFragment = React.FC<InvoiceAttachmentProps> & {
	fragments: {
		invoiceAttachmentInvoiceFragment: DocumentNode;
	};
};

export const InvoiceAttachment: InvoiceAttachmentPropsWithFragment = ( {
	invoice,
	guestToken,
	fileOrFilename,
	className,
} ) => {
	const [ getInvoiceWhere ] = useInvoiceAttachment_GetInvoiceWhereLazyQuery();

	const attachmentExtension =
		typeof invoice?.uploadAttachment === 'string'
			? invoice.uploadAttachment.split( '.' ).pop()?.toUpperCase()
			: fileOrFilename instanceof File
				? fileOrFilename.name.split( '.' ).pop()?.toUpperCase()
				: fileOrFilename?.split( '.' ).pop()?.toUpperCase();

	const getAttachmentUrlMethod = useCallback( async () => {
		if ( !invoice?.id ) {
			throw new Error( 'Invoice ID required in order to allow uploads' );
		}
		const res = await getInvoiceWhere( {
			variables: {
				where: {
					id: invoice.id,
				},
				...( guestToken
					? {
						data: {
							token: guestToken,
						},
					  }
					: {} ),
			},
		} );
		return {
			url: res?.data?.getInvoiceWhere?.invoice.attachmentUrl,
			...( res.error ? { errors: [ res?.error ] } : {} ),
		};
	}, [
		invoice?.id,
		getInvoiceWhere,
		guestToken
	] );

	const nonAttachmentFilename =
		typeof fileOrFilename === 'string'
			? fileOrFilename
			: fileOrFilename instanceof File
				? fileOrFilename.name
				: undefined;
	const nonAttachmentIconType = getIconTypeForDocument(
		{ fileType: attachmentExtension },
		true
	);

	if ( !invoice?.uploadAttachment && !nonAttachmentFilename ) {
		return null;
	}

	return (
		<Box className={ classNames( styles.container, className ) }>
			<Typography variant='h6' data-cy='attachment-label'>
				Attachment
			</Typography>
			{ invoice?.uploadAttachment ? (
				<Attachment
					getUrlMethod={ getAttachmentUrlMethod }
					fileType={ attachmentExtension }
				>
					<Typography className={ styles.link }>
						{ getFilename( invoice.uploadAttachment ) }
					</Typography>
				</Attachment>
			) : nonAttachmentFilename ? (
				<Typography className={ styles.nonLink }>
					<Icon type={ nonAttachmentIconType } className={ styles.icon } />
					{ nonAttachmentFilename }
				</Typography>
			) : null }
		</Box>
	);
};

InvoiceAttachment.fragments = {
	invoiceAttachmentInvoiceFragment,
};
